import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Alert, Button, Card, Input } from '@/components/ui';
import { useAnnouncer } from '@/lib/a11y';
import { sendOtp, updateMyName, verifyOtp } from '@/lib/api/auth';
import { useApiMutation } from '@/lib/api/queryClient';
import type { CustomerAuthResponse, CustomerProfile, OtpSendResponse } from '@/lib/api/types';
import {
  normalisePhone,
  otpFormSchema,
  otpVerifySchema,
  profileNameSchema,
  type OtpFormValues,
  type OtpVerifyValues,
  type ProfileNameValues,
} from '@/lib/validators';

import { useSession } from './sessionContext';
import { formatCooldown, useResendCooldown } from './useResendCooldown';

export interface OtpFormProps {
  /** Called after a successful verify, with the fresh profile already in context. */
  onSignedIn?: () => void;
}

/**
 * Phone + OTP sign-in.
 *
 * Two properties this form must not break:
 *
 *   1. IT NEVER NAVIGATES. The customer arrives here with `?u=` (and usually
 *      `?q=`) in the URL, and their upload belongs to an anonymous session
 *      cookie that the verify call reassigns to the new account. Redirecting to
 *      a /login route and back would drop those ids from the client's side even
 *      though the server kept the file. So sign-in happens in place, and the URL
 *      is untouched from first render to signed-in.
 *   2. IT SAYS THE SAME THING FOR KNOWN AND UNKNOWN NUMBERS. The backend answers
 *      identically on purpose so the endpoint is not a customer-list oracle; a
 *      "welcome back" here would undo that.
 */
export function OtpForm({ onSignedIn }: OtpFormProps) {
  const { signIn } = useSession();
  const { announce } = useAnnouncer();
  const cooldown = useResendCooldown();

  const [phone, setPhone] = useState<string | null>(null);
  const [debugCode, setDebugCode] = useState<string | null>(null);
  const codeInputRef = useRef<HTMLInputElement | null>(null);
  const nameInputRef = useRef<HTMLInputElement | null>(null);

  const phoneForm = useForm<OtpFormValues>({
    resolver: zodResolver(otpFormSchema),
    defaultValues: { phone: '' },
    mode: 'onSubmit',
  });

  const codeForm = useForm<OtpVerifyValues>({
    resolver: zodResolver(otpVerifySchema),
    defaultValues: { code: '' },
    mode: 'onSubmit',
  });

  const nameForm = useForm<ProfileNameValues>({
    resolver: zodResolver(profileNameSchema),
    defaultValues: { name: '' },
    mode: 'onSubmit',
  });

  /**
   * The verified session, HELD BACK until we have a name.
   *
   * Not signed in yet on purpose. The parent swaps this whole form out the
   * moment the session turns authenticated — DetailsStep replaces it with a
   * "Signed in" card — so calling `signIn` here would unmount the name step
   * before it ever rendered. The access token is already in the API client
   * (verify put it there), which is all the PATCH needs; the context is told
   * once the name is in.
   */
  const [pendingName, setPendingName] = useState<CustomerAuthResponse | null>(null);

  const send = useApiMutation<OtpSendResponse, string>({
    mutationFn: (nextPhone) => sendOtp(nextPhone),
    onSuccess: (response, nextPhone) => {
      setPhone(nextPhone);
      setDebugCode(response.debug_code ?? null);
      cooldown.start(response.resend_in_seconds);
      announce(`We sent a code to ${nextPhone}. It expires in ten minutes.`);
    },
    onError: (error) => {
      // A 429 tells us how long to wait; honour it rather than letting the
      // customer hammer a button that will keep refusing.
      const retryAfter = error.retryAfterSeconds;
      if (retryAfter) cooldown.start(retryAfter);
      const fieldError = error.fieldErrors['phone'];
      if (fieldError) phoneForm.setError('phone', { message: fieldError });
    },
  });

  const verify = useApiMutation<CustomerAuthResponse, OtpVerifyValues>({
    mutationFn: (values) => verifyOtp({ phone: phone ?? '', code: values.code }),
    onSuccess: (response) => {
      if (response.user.name) {
        signIn(response.user, response.expires_in_seconds);
        announce('Signed in.');
        onSignedIn?.();
        return;
      }
      // A brand-new account. Ask once, here, rather than every time forever.
      setPendingName(response);
      announce('Code accepted. We just need a name for your parcel.');
    },
    onError: (error) => {
      const fieldError = error.fieldErrors['code'];
      if (fieldError) codeForm.setError('code', { message: fieldError });
    },
  });

  const saveName = useApiMutation<CustomerProfile, ProfileNameValues>({
    mutationFn: (values) => updateMyName(values.name),
    onSuccess: (profile) => {
      signIn(profile, pendingName?.expires_in_seconds ?? 0);
      announce('Thanks. Signed in.');
      onSignedIn?.();
    },
    onError: (error) => {
      const fieldError = error.fieldErrors['name'];
      if (fieldError) nameForm.setError('name', { message: fieldError });
    },
  });

  // Moving focus to the code box is the difference between "type the code" and
  // "find the box, then type the code" for keyboard and screen-reader users.
  useEffect(() => {
    if (phone) codeInputRef.current?.focus();
  }, [phone]);

  useEffect(() => {
    if (pendingName) nameInputRef.current?.focus();
  }, [pendingName]);

  if (!phone) {
    return (
      <Card title="Your mobile number">
        <form
          noValidate
          onSubmit={(event) => {
            void phoneForm.handleSubmit((values) => {
              send.mutate(normalisePhone(values.phone));
            })(event);
          }}
          className="flex flex-col gap-4"
        >
          {send.isError && send.error && !phoneForm.formState.errors.phone ? (
            <Alert severity="danger" title="We could not send the code" live>
              <p>{send.error.message}</p>
            </Alert>
          ) : null}

          <Input
            {...phoneForm.register('phone')}
            label="Mobile number"
            type="tel"
            inputMode="numeric"
            autoComplete="tel-national"
            prefix="+91"
            placeholder="9876543210"
            maxLength={13}
            required
            hint="We text you a 6-digit code. No password to remember."
            error={phoneForm.formState.errors.phone?.message}
          />

          <Button
            type="submit"
            fullWidth
            loading={send.isPending}
            loadingText="Sending the code…"
            disabled={cooldown.active}
          >
            {cooldown.active ? `Try again in ${formatCooldown(cooldown.secondsLeft)}` : 'Send code'}
          </Button>
        </form>
      </Card>
    );
  }

  if (pendingName) {
    const { ref: nameRef, ...nameField } = nameForm.register('name');

    return (
      <Card title="What should we call you?">
        <form
          noValidate
          onSubmit={(event) => {
            void nameForm.handleSubmit((values) => saveName.mutate(values))(event);
          }}
          className="flex flex-col gap-4"
        >
          <p className="text-sm text-ink-muted">
            That code was right. We print this on the parcel so the courier knows who it is for.
          </p>

          {saveName.isError && saveName.error && !nameForm.formState.errors.name ? (
            <Alert severity="danger" title="We could not save that" live>
              <p>{saveName.error.message}</p>
            </Alert>
          ) : null}

          <Input
            {...nameField}
            ref={(element) => {
              nameRef(element);
              nameInputRef.current = element;
            }}
            label="Your name"
            autoComplete="name"
            required
            error={nameForm.formState.errors.name?.message}
          />

          <Button type="submit" fullWidth loading={saveName.isPending} loadingText="Saving…">
            Continue
          </Button>
        </form>
      </Card>
    );
  }

  const { ref: codeRef, ...codeField } = codeForm.register('code');

  return (
    <Card title="Enter the code">
      <form
        noValidate
        onSubmit={(event) => {
          void codeForm.handleSubmit((values) => verify.mutate(values))(event);
        }}
        className="flex flex-col gap-4"
      >
        <p className="text-sm text-ink-muted">
          We sent a 6-digit code to <strong className="text-ink">+91 {phone}</strong>.{' '}
          <button
            type="button"
            onClick={() => {
              setPhone(null);
              setDebugCode(null);
              codeForm.reset();
            }}
            className="min-h-touch font-medium text-brand-ink underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2"
          >
            Change number
          </button>
        </p>

        {verify.isError && verify.error && !codeForm.formState.errors.code ? (
          <Alert severity="danger" title="That did not work" live>
            <p>{verify.error.message}</p>
          </Alert>
        ) : null}

        {debugCode ? (
          // Only ever present when the API is running with DEBUG_EXPOSE_OTP on.
          <Alert severity="info" title="Development mode">
            <p>
              Your code is <strong>{debugCode}</strong>. This is only shown because the API is in
              debug mode.
            </p>
          </Alert>
        ) : null}

        <Input
          {...codeField}
          ref={(element) => {
            codeRef(element);
            codeInputRef.current = element;
          }}
          label="6-digit code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          required
          error={codeForm.formState.errors.code?.message}
        />

        <Button type="submit" fullWidth loading={verify.isPending} loadingText="Checking the code…">
          Verify and continue
        </Button>

        <Button
          type="button"
          variant="ghost"
          fullWidth
          disabled={cooldown.active || send.isPending}
          loading={send.isPending}
          loadingText="Sending…"
          onClick={() => send.mutate(phone)}
        >
          {cooldown.active
            ? `Resend code in ${formatCooldown(cooldown.secondsLeft)}`
            : 'Resend code'}
        </Button>
      </form>
    </Card>
  );
}
